/**
 * Token Manager for Upload Service
 *
 * Manages fetching and caching of uploader JWT tokens.
 * The upload service requires a JWT token separate from the regular user token.
 */

import AXIOS from 'axios';
import endpoints from './endpoints.json';
import baseUrl from './baseURL.json';

// Standalone logger to avoid circular dependency with Api/errorLogger
const LOG_ENDPOINT = 'https://zoiteckh.com/api/public/projector-error/';
const logTokenError = (payload) => {
  try {
    AXIOS.post(LOG_ENDPOINT, {
      website: 6,
      user_name: payload?.userName || 'Guest',
      function_affected: payload?.functionAffected || 'Token Refresh',
      description: payload?.description || 'Unknown error',
      severity: payload?.severity || 'high',
      browser_info: navigator?.userAgent || '',
      os_info: navigator?.platform || '',
      screen_resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      page_url: window?.location?.href || '',
      raw_data: {
        error_message: payload?.errorMessage || null,
        error_stack: payload?.errorStack || null,
        response_status: payload?.responseStatus || null,
        response_body: payload?.responseBody || null,
        request_url: payload?.requestUrl || null,
        request_method: payload?.requestMethod || null,
        timestamp: new Date().toISOString(),
        component: payload?.component || null,
        user_action: payload?.userAction || null,
      },
    }).catch(() => {}); // Fire and forget
  } catch (e) {
    // Silently ignore any logging errors - never break upload flow
  }
};

const UPLOADER_TOKEN_KEY = 'uploader_token';
const TOKEN_EXPIRY_KEY = 'uploader_token_expiry';

// Get API base URL based on environment
const getApiBaseUrl = () => {
  if (typeof window === 'undefined') return baseUrl.PRODUCTION;

  const { origin } = window.location;

  if (origin.includes('localhost')) {
    return baseUrl.PRODUCTION; // Use production backend for localhost testing
  }
  if (origin.includes('preprod')) {
    return baseUrl.PRODUCTION;
  }
  if (origin.includes('dev.app')) {
    return baseUrl.DEVELOPMENT_URL;
  }
  return baseUrl.PRODUCTION;
};

// Create axios instance for API calls
const axios = AXIOS.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Check if token is expired or about to expire (within 5 minutes)
 */
function isTokenExpired() {
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryStr) return true;

  const expiry = parseInt(expiryStr, 10);
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;

  return expiry - now < fiveMinutes;
}

/**
 * Parse JWT token to extract expiry time
 * @param {string} token - JWT token
 * @returns {number|null} Expiry timestamp in milliseconds, or null if invalid
 */
function parseTokenExpiry(token) {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decode payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    // 'exp' is in seconds, convert to milliseconds
    if (payload.exp) {
      return payload.exp * 1000;
    }

    return null;
  } catch (error) {
    console.error('[TokenManager] Failed to parse token expiry:', error);
    return null;
  }
}

/**
 * Validate token format (must be JWT)
 * @param {string} token - Token to validate
 * @returns {boolean} True if valid JWT format
 */
export function isValidJWT(token) {
  if (!token || typeof token !== 'string') return false;

  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    // Try to decode the payload
    const payload = JSON.parse(atob(parts[1]));
    return !!payload;
  } catch {
    return false;
  }
}

/**
 * Fetch a new uploader token from the backend
 *
 * The upload token is returned in the `api_response.access` field from:
 * - POST /v4/signin (during login)
 * - POST /getMyProfile (when fetching user profile)
 *
 * This matches the mobile app's approach where upload token comes from signin response.
 *
 * @returns {Promise<string>} JWT token for upload service
 */
async function fetchUploaderToken() {
  const userName = `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest';

  try {
    console.log('[TokenManager] Fetching uploader token from backend...');

    // Check if user is logged in
    const userToken = localStorage.getItem('token');
    const cachedUploaderToken = localStorage.getItem('uploader_token');

    if (!userToken) {
      const error = new Error('No user token found. Please log in first.');
      logTokenError({
        userName,
        functionAffected: 'Token Refresh',
        description: 'Upload token fetch failed — no user token in localStorage',
        severity: 'high',
        errorMessage: error.message,
        responseBody: JSON.stringify({
          hasUserToken: false,
          hasCachedUploaderToken: !!cachedUploaderToken,
          cachedUploaderToken: cachedUploaderToken ? `${cachedUploaderToken.substring(0, 30)}...` : null,
          localStorageKeys: Object.keys(localStorage),
        }),
        component: 'tokenManager',
        userAction: 'fetchUploaderToken - no user token',
      });
      throw error;
    }

    // Primary: Use /auth/get-token/ on cloud/uploader service (same as gift flow success page)
    const cloudBaseUrl = window.location.origin.includes('dev.app')
      ? 'https://cloud.eq7.in/api'
      : 'https://uploader.projector.app/api';

    try {
      const cloudResponse = await AXIOS.post(
        `${cloudBaseUrl}/auth/get-token/`,
        { token: userToken },
        { headers: { 'Content-Type': 'application/json' } },
      );
      if (cloudResponse?.data?.access) {
        console.log('[TokenManager] Upload token fetched via /auth/get-token/');
        return cloudResponse.data.access;
      }
    } catch (err) {
      console.warn('[TokenManager] /auth/get-token/ failed, trying getMyProfile fallback:', err.message);

      // Log the /auth/get-token/ failure with full details
      logTokenError({
        userName,
        functionAffected: 'Token Refresh',
        description: `Upload token fetch failed — /auth/get-token/ returned error: ${err.message}`,
        severity: 'medium',
        errorMessage: err.message,
        errorStack: err.stack,
        responseStatus: err.response?.status || null,
        responseBody: JSON.stringify({
          error: err.response?.data,
          requestPayload: { token: userToken ? `${userToken.substring(0, 20)}...` : null },
          cachedUploaderToken: localStorage.getItem('uploader_token') ? `${localStorage.getItem('uploader_token').substring(0, 20)}...` : null,
          hasUserToken: !!userToken,
          userTokenLength: userToken?.length || 0,
        }),
        requestUrl: `${cloudBaseUrl}/auth/get-token/`,
        requestMethod: 'POST',
        component: 'tokenManager',
        userAction: 'fetchUploaderToken - /auth/get-token/ call',
      });
    }

    // Fallback: Call getMyProfile to get fresh upload token from api_response.access
    let response = null;
    try {
      response = await axios.post(endpoints.getMyProfile, { token: userToken });
      console.log('[TokenManager] Profile response received');
    } catch (err) {
      // Log getMyProfile failure with full details
      logTokenError({
        userName,
        functionAffected: 'Token Refresh',
        description: `Upload token fetch failed — getMyProfile returned error: ${err.message}`,
        severity: 'high',
        errorMessage: err.message,
        errorStack: err.stack,
        responseStatus: err.response?.status || null,
        responseBody: JSON.stringify({
          error: err.response?.data,
          requestPayload: { token: userToken ? `${userToken.substring(0, 30)}...` : null },
          hasUserToken: !!userToken,
          userTokenLength: userToken?.length || 0,
          hasCachedUploaderToken: !!cachedUploaderToken,
        }),
        requestUrl: endpoints.getMyProfile,
        requestMethod: 'POST',
        component: 'tokenManager',
        userAction: 'fetchUploaderToken - getMyProfile fallback',
      });
      throw err;
    }

    // Extract upload token from api_response.access (matches mobile app)
    const uploaderToken = response.data?.api_response?.access;

    if (!uploaderToken) {
      console.warn('[TokenManager] No api_response.access in profile response:', {
        hasApiResponse: !!response.data?.api_response,
        responseKeys: Object.keys(response.data || {}),
      });

      // Log missing token in response with full details
      logTokenError({
        userName,
        functionAffected: 'Token Refresh',
        description: 'Upload token fetch failed — no api_response.access in getMyProfile response',
        severity: 'high',
        errorMessage: 'Upload token not found in api_response.access',
        responseBody: JSON.stringify({
          hasApiResponse: !!response.data?.api_response,
          responseKeys: Object.keys(response.data || {}),
          apiResponseKeys: Object.keys(response.data?.api_response || {}),
          userTokenUsed: userToken ? `${userToken.substring(0, 30)}...` : null,
          userTokenLength: userToken?.length || 0,
          responseSuccess: response.data?.success,
          responseMessage: response.data?.message,
        }),
        requestUrl: endpoints.getMyProfile,
        requestMethod: 'POST',
        component: 'tokenManager',
        userAction: 'fetchUploaderToken - missing access token',
      });

      // Fallback: Try using user token if it's JWT format
      if (userToken.split('.').length === 3) {
        console.log('[TokenManager] Fallback: Using user token (JWT format)');
        return userToken;
      }

      throw new Error(
        'Upload token not found in api_response.access. Backend may need to be updated.'
      );
    }

    // Validate it's a JWT token
    if (!isValidJWT(uploaderToken)) {
      console.error('[TokenManager] Invalid upload token format:', uploaderToken.substring(0, 50));

      logTokenError({
        userName,
        functionAffected: 'Token Refresh',
        description: `Upload token fetch failed — token is not valid JWT format: ${uploaderToken.substring(0, 30)}...`,
        severity: 'high',
        errorMessage: 'Upload token is not in JWT format',
        component: 'tokenManager',
        userAction: 'fetchUploaderToken - invalid JWT',
      });

      throw new Error('Upload token is not in JWT format');
    }

    console.log('[TokenManager] Upload token fetched successfully');

    // Also save user UUID if present (used for S3 paths)
    const userUuid = response.data?.api_response?.uuid;
    if (userUuid) {
      localStorage.setItem('user_uuid', userUuid);
      console.log('[TokenManager] User UUID saved:', userUuid);
    }

    return uploaderToken;
  } catch (error) {
    console.error('[TokenManager] Failed to fetch uploader token:', error);
    throw error;
  }
}

/**
 * Get valid uploader token (from cache or fetch new one)
 *
 * @returns {Promise<string>} Valid JWT token for upload service
 */
export async function getUploaderToken() {
  try {
    // Use cached token if present — trust server to reject expired tokens with
    // 401, which the response interceptor handles by clearing + refetching.
    const cachedToken = localStorage.getItem(UPLOADER_TOKEN_KEY);

    if (cachedToken) {
      console.log('[TokenManager] Using cached uploader token');
      return cachedToken;
    }

    // No cached token — fetch a new one
    console.log('[TokenManager] No cached token, fetching new one...');
    const newToken = await fetchUploaderToken();

    // Parse expiry from token
    const expiry = parseTokenExpiry(newToken);

    // Cache the new token
    localStorage.setItem(UPLOADER_TOKEN_KEY, newToken);
    if (expiry) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      console.log('[TokenManager] Token cached, expires at:', new Date(expiry).toISOString());
    }

    return newToken;
  } catch (error) {
    console.error('[TokenManager] Failed to get uploader token:', error);
    throw error;
  }
}

/**
 * Clear cached uploader token (call on logout or token error)
 */
export function clearUploaderToken() {
  localStorage.removeItem(UPLOADER_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  console.log('[TokenManager] Uploader token cleared');
}

export default { getUploaderToken, clearUploaderToken, isValidJWT };
